/**
 * NewProjectModal: shared "New Project" modal used by the Dashboard and the
 * canonical Projects page. extracted so both entry points share exactly the
 * same form (was inlined into DashboardPage).
 *
 * if `droppedFile` is provided the submit button becomes "Create & Import"
 * and on success the caller navigates into the import workflow.
 */

import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/contexts/ToastContext'
import { projectsApi, Project, Shipyard, ComplianceRuleProfile } from '@/lib/projectsApi'
import { ComplianceProfileSelector } from '@/components/projects/ComplianceProfileSelector'
import { useTranslation } from 'react-i18next'

export interface NewProjectModalProps {
  open: boolean
  droppedFile: File | null
  initialShipyardId?: number | ''
  onClose: () => void
  /** Called with the newly-created project. if `droppedFile` is set the caller
   *  usually navigates to the import page, otherwise refresh the list. */
  onCreated: (project: Project, droppedFile: File | null) => void
}

export const NewProjectModal: React.FC<NewProjectModalProps> = ({
  open,
  droppedFile,
  initialShipyardId = '',
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()

  const [shipyards, setShipyards] = useState<Shipyard[]>([])
  const [complianceProfiles, setComplianceProfiles] = useState<ComplianceRuleProfile[]>([])

  const [hull,          setHull]          = useState('')
  const [shipyardId,    setShipyardId]    = useState<number | ''>('')
  const [vesselName,    setVesselName]    = useState('')
  const [vesselType,    setVesselType]    = useState('')   // ship type, populates the RA cover
  const [imo,           setImo]           = useState('')
  const [selectedProfileCodes, setSelectedProfileCodes] = useState<string[]>([])
  const [submitting,    setSubmitting]    = useState(false)

  // lazy-load lookups the first time the modal opens
  useEffect(() => {
    if (!open) return
    if (shipyards.length === 0) projectsApi.listShipyards().then(setShipyards).catch(() => {})
    if (complianceProfiles.length === 0) {
      projectsApi.listComplianceProfiles().then(setComplianceProfiles).catch(() => {})
    }
  }, [open, shipyards.length, complianceProfiles.length])

  // reset form each time the modal re-opens
  useEffect(() => {
    if (open) {
      setHull('')
      setShipyardId(initialShipyardId)
      setVesselName('')
      setVesselType('')
      setImo('')
      setSelectedProfileCodes([])
    }
  }, [open, initialShipyardId])

  if (!open) return null

  const handleSubmit = async () => {
    if (!hull.trim() || selectedProfileCodes.length === 0) return
    setSubmitting(true)
    try {
      const project = await projectsApi.createProject({
        hull_number:    hull.trim(),
        shipyard_id:    shipyardId !== '' ? shipyardId : null,
        vessel_name:    vesselName.trim() || null,
        imo_number:     imo.trim() || null,
        vessel_type:    vesselType.trim() || null,
        ship_owner:     null,
        project_name:   null,
        status:         'active',
        compliance_profiles: selectedProfileCodes.map(profile_code => ({ profile_code })),
      })
      success(t('projectCreate.created'))
      onCreated(project, droppedFile)
    } catch (err: any) {
      showError(err.message ?? t('projectCreate.createFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[2px]">
      <div role="dialog" aria-modal="true" aria-labelledby="new-project-title" className="w-full max-w-lg overflow-hidden rounded-lg border border-slate-300 bg-white shadow-[0_24px_64px_rgba(15,23,42,0.24)] dark:bg-slate-800">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <h2 id="new-project-title" className="text-lg font-semibold tracking-[-0.01em]">{t('projectCreate.title')}</h2>
            {droppedFile && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate max-w-[340px]">
                {droppedFile.name}
              </p>
            )}
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:text-slate-200" aria-label={t('projectCreate.closeDialog')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[65vh] space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('reports.hullNumber')} <span className="text-red-500">*</span>
            </label>
            <input
              value={hull}
              onChange={e => setHull(e.target.value)}
              placeholder="e.g. H-1234"
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-600 dark:bg-slate-700 dark:text-white placeholder:text-slate-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('reports.shipyard')}</label>
            <select
              value={shipyardId}
              onChange={e => setShipyardId(e.target.value === '' ? '' : Number(e.target.value))}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            >
              <option value="">{t('projectCreate.selectShipyard')}</option>
              {shipyards.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('reports.vesselName')}</label>
            <input
              value={vesselName}
              onChange={e => setVesselName(e.target.value)}
              placeholder="e.g. MV Sea Hawk"
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-600 dark:bg-slate-700 dark:text-white placeholder:text-slate-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('projectCreate.vesselType')}</label>
            <input
              value={vesselType}
              onChange={e => setVesselType(e.target.value)}
              placeholder="e.g. S-MAX Shuttle Tanker"
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-600 dark:bg-slate-700 dark:text-white placeholder:text-slate-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('projectCreate.imoNumber')}</label>
            <input
              value={imo}
              onChange={e => setImo(e.target.value)}
              placeholder="e.g. 9876543"
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition hover:border-slate-400 focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-600 dark:bg-slate-700 dark:text-white placeholder:text-slate-400"
            />
          </div>

          {complianceProfiles.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('projectDetail.classificationProfiles')} <span className="text-red-500">*</span>
              </label>
              <ComplianceProfileSelector
                profiles={complianceProfiles}
                selectedCodes={selectedProfileCodes}
                onChange={setSelectedProfileCodes}
                disabled={submitting}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50/70 px-6 py-3 dark:border-slate-700 dark:bg-slate-900/20">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={!hull.trim() || !selectedProfileCodes.length || submitting}>
            {submitting ? t('projectCreate.creating') : droppedFile ? t('projectCreate.createAndImport') : t('projectCreate.create')}
          </Button>
        </div>
      </div>
    </div>
  )
}
